import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, Users } from 'lucide-react';
import type { WizardFormData } from '../types';
import type { SchoolOptions } from '@/types/school';

interface ConfigStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  options: SchoolOptions | null;
}

export function ConfigStep({ formData, onChange, options }: ConfigStepProps) {
  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-foreground">Configuration</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configurez les paramètres généraux de l'école
        </p>
      </div>

      <div className="grid gap-6">
        {/* Type and Support */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="h-4 w-4 text-primary" />
              Type et support
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type d'école</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => onChange({ type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support">Niveau de support</Label>
                <Select
                  value={formData.support}
                  onValueChange={(value) => onChange({ support: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner le support" />
                  </SelectTrigger>
                  <SelectContent>
                    {options?.supports.map((support) => (
                      <SelectItem key={support.value} value={support.value}>
                        {support.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="vip"
                  checked={formData.vip}
                  onCheckedChange={(checked) =>
                    onChange({ vip: checked as boolean })
                  }
                />
                <Label htmlFor="vip" className="text-sm font-normal">
                  Client VIP
                </Label>
              </div>


            </div>
          </CardContent>
        </Card>

        {/* Contacts */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              Contacts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="retailer">Apporteur d'affaires</Label>
                <Select
                  value={formData.retailerId?.toString() || 'none'}
                  onValueChange={(value) =>
                    onChange({ retailerId: value === 'none' ? null : parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {options?.users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.fullname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="technicalContact">Contact technique</Label>
                <Select
                  value={formData.technicalContactId?.toString() || 'none'}
                  onValueChange={(value) =>
                    onChange({
                      technicalContactId: value === 'none' ? null : parseInt(value),
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {options?.users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.fullname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="salesContact">Contact commercial</Label>
                <Select
                  value={formData.salesContactId?.toString() || 'none'}
                  onValueChange={(value) =>
                    onChange({ salesContactId: value === 'none' ? null : parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {options?.users.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.fullname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
